import {useCallback, useState, useSyncExternalStore, type ReactNode} from 'react'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {noop} from '@github-ui/noop'
import {useRouteParams} from '@github-ui/react-core/future/use-route-params'
import type {RouteMatchData} from '@github-ui/react-core/future/query-route'
import {useStreamingCompatibleQuery} from '@github-ui/react-core/future/use-streaming-compatible-query'
import {skipToken, useQueryClient, type UseQueryOptions} from '@github-ui/react-query'
import {isQueryDataRestored} from '@github-ui/react-query-persister'
import {useRouteLoaderData} from '@github-ui/react-router'
import {DeferredCopilotReviewEffortContext} from '@github-ui/mergebox/use-mark-ready-for-review-mutation'
import {
  DeferredType,
  type ReviewRequestsResponse,
} from '@github-ui/dotcom-schema/types/pull_requests/v1/review_requests'
import {pullRequestsLayoutRoute} from '../../../../routes/layout-route'
import {pullRequestsOverviewIndexRoute} from '../../../../routes/overview-route'
import {useActiveRoute} from '../../../../hooks/use-active-route'
import {isCopilotPullRequestReviewer, matchesCopilotReviewerRow} from '../copilot-reviewer'
import {normalizeCopilotReviewEffort} from './copilot-review-effort'
import {useCopilotReviewEffortDecision} from './use-copilot-review-effort-decision'
import {CopilotReviewEffortContext} from './use-shared-copilot-review-effort'

const noSubscription = () => noop

export function CopilotReviewEffortProvider({children}: {children: ReactNode}) {
  const queryClient = useQueryClient()
  const activeRoute = useActiveRoute()
  const active = activeRoute === 'overview'
  const {owner, repo, pr_number} = useRouteParams(pullRequestsLayoutRoute)
  const login = useSyncExternalStore(noSubscription, getCurrentUserLogin, getCurrentUserLogin)
  const scope = JSON.stringify([login, owner, repo, pr_number])
  const route = pullRequestsOverviewIndexRoute
  const loader = useRouteLoaderData<RouteMatchData<typeof route>>(route.id)
  const query = active ? loader?.queries?.review_requests : undefined
  const queryState = query ? queryClient.getQueryState(query.queryKey) : undefined
  const revalidatingUpdateCount =
    queryState?.data !== undefined && queryState.fetchStatus === 'fetching' ? queryState.dataUpdateCount : undefined
  const querySnapshot = {scope, route, query, revalidatingUpdateCount}
  const [previousQuery, setPreviousQuery] = useState(querySnapshot)
  const queryChanged = previousQuery.scope !== scope || (query && query !== previousQuery.query)
  if (queryChanged) {
    setPreviousQuery(querySnapshot)
  }
  const readyQuery = queryChanged ? querySnapshot : previousQuery.scope === scope ? previousQuery : undefined
  // The provider must remain mounted when Overview is not the active child route.
  // Track commits even when structural sharing keeps the cached data object.
  const {data, dataUpdatedAt, isPlaceholderData} = useStreamingCompatibleQuery<ReviewRequestsResponse>(
    readyQuery?.query
      ? (readyQuery.route.buildQueryOptions(
          'review_requests',
          readyQuery.query.queryKey,
        ) as UseQueryOptions<ReviewRequestsResponse>)
      : {queryKey: ['route-data-unavailable', route.id, 'review_requests'], queryFn: skipToken},
    readyQuery?.query?.suppressDuringSSR ?? false,
  )
  const pendingRequest = data?.pendingReviewRequests.find(
    request =>
      isCopilotPullRequestReviewer(request.reviewer) &&
      matchesCopilotReviewerRow(request.reviewer, data.copilotReviewerRow),
  )
  // Quota dismissal can remove a request without changing its effort history.
  const requestWithSavedEffort = normalizeCopilotReviewEffort(pendingRequest?.requestedCopilotReviewEffort)
    ? pendingRequest
    : undefined
  const completedReview = data?.completedReviews.find(review =>
    matchesCopilotReviewerRow(review.reviewer, data.copilotReviewerRow),
  )
  const completedReviewId = completedReview?.id.toString()
  const reviewState = data
    ? JSON.stringify([requestWithSavedEffort?.deferredType, requestWithSavedEffort?.createdAt, completedReviewId])
    : undefined
  const cachedQuery = readyQuery?.query
    ? queryClient.getQueryCache().find({queryKey: readyQuery.query.queryKey, exact: true})
    : undefined
  const restored = isQueryDataRestored(cachedQuery)
  // A navigation can show old in-memory data while revalidating. Only a successful commit ends that handoff.
  const revalidatingMemory =
    readyQuery?.revalidatingUpdateCount !== undefined &&
    cachedQuery?.state.dataUpdateCount === readyQuery.revalidatingUpdateCount
  // Committed cache updates, including direct writes, remain fresh while another fetch runs.
  const dataSource =
    restored || revalidatingMemory
      ? 'cached'
      : data && dataUpdatedAt !== 0 && !isPlaceholderData
        ? 'network'
        : undefined
  const effort = useCopilotReviewEffortDecision(
    data?.copilotReviewerRow,
    scope,
    reviewState,
    completedReviewId,
    dataSource,
  )
  const deferred = pendingRequest?.deferredType === DeferredType.COPILOT
  const captureReady = useCallback(() => (deferred ? effort.captureRequest() : undefined), [deferred, effort])

  return (
    <CopilotReviewEffortContext value={active ? effort : null}>
      <DeferredCopilotReviewEffortContext
        value={readyQuery?.query && activeRoute !== 'conversation' ? captureReady : null}
      >
        {children}
      </DeferredCopilotReviewEffortContext>
    </CopilotReviewEffortContext>
  )
}
